import Phaser from 'phaser';

import * as C from 'tower/constants';
import { EntityConfig, Flags, SharedStateModification, WorkerMessage } from 'tower/types';
import { Direction } from '../util';
import SharedState from './shared-state';
import FLOOR1 from 'tower/scenes/battle/floors';
import worker from './worker';

type SwordKey = 'sword' | 'whiteSword' | 'masterSword';

/**
 * JSON-serializable representation of GameState values that we want to save, but not share between
 * multiple open tabs.
 */
interface PersistedGameState {
  _subweapon: C.ItemKey | null;
  _checkpointRoomKey: string;
  _volume: number;
}

export class GameState extends Phaser.Events.EventEmitter {
  private _hp = 6;
  private _score = 0;
  private _subweapon: C.ItemKey | null = null;
  private _mattock_durability = 0;
  private _candle_durability = 3;
  private _roomHistory: string[] = [];
  private _currentRoomKey: string = 'start';
  private _checkpointRoomKey: string = 'start';
  private _subweaponsDisabled = false;
  private _volume = C.DEFAULT_VOLUME;
  public stateInitComplete = false;
  public overridden = false;

  constructor(private sharedState: SharedState) {
    super();

    // Forward events from shared storage
    for (const event of [
      'itemchange',
      'roomitemchange',
      'flagchange',
      'openeddoorschanged',
      'foundkeychange',
      'keycountchange',
      'solvedroomschange',
    ]) {
      sharedState.on(event, (...args: any) => this.emit(event, ...args));
    }
  }

  toJSON(): PersistedGameState {
    return {
      _subweapon: this._subweapon,
      _checkpointRoomKey: this._checkpointRoomKey,
      _volume: this._volume,
    };
  }

  applyPersisted(state: PersistedGameState) {
    this._subweapon = state._subweapon;
    this._checkpointRoomKey = state._checkpointRoomKey;
    this.volume = state._volume ?? C.DEFAULT_VOLUME;
  }

  persist() {
    if (this.overridden) {
      return;
    }
    localStorage.setItem(C.GAMESTORAGE_KEY, JSON.stringify(this.toJSON()));
  }

  applyOverride(override: C.GameStateOverride) {
    if (override.items) {
      this.sharedState.items = new Set(override.items);
    }
    if (override.subweapon) {
      this.subweapon = override.subweapon;
    }
    if (override.hp) {
      this.hp = override.hp;
    }
    if (override.checkpointRoomKey) {
      this._checkpointRoomKey = override.checkpointRoomKey;
    }
    if (override.flags) {
      this.sharedState.flags = structuredClone(override.flags);
    }

    this.overridden = true;
  }

  async hasSave() {
    return this.sharedState.hasSave();
  }

  get hp() {
    return this._hp;
  }

  set hp(value: number) {
    this._hp = Math.min(value, this.maxHp);
    this.emit('healthchange', value, this.maxHp);
  }

  get maxHp() {
    let maxHp = 6;
    if (this.hasItem('heart1')) {
      maxHp += 2;
    }
    if (this.hasItem('heart2')) {
      maxHp += 2;
    }
    if (this.hasItem('heart3')) {
      maxHp += 2;
    }
    return maxHp;
  }

  get score() {
    return this._score;
  }

  set score(value: number) {
    this._score = value;
    this.emit('scorechange', value);
  }

  get subweapon() {
    // Fallback: If the subweapon is invalid, fall back to the first valid subweapon we have
    if (!this.items.has(this._subweapon!)) {
      for (const itemKey of C.INVENTORY_DISPLAY_ITEMS) {
        if (this.items.has(itemKey)) {
          this._subweapon = itemKey;
          return itemKey;
        }
      }

      return null;
    }

    return this._subweapon;
  }

  set subweapon(value: C.ItemKey | null) {
    this._subweapon = value;
    this.emit('subweaponchange', value);
    this.persist();
  }

  get sword(): SwordKey | null {
    for (const key of ['masterSword', 'whiteSword', 'sword']) {
      if (this.sharedState.items.has(key)) {
        return key as SwordKey;
      }
    }
    return null;
  }

  get items() {
    return this.sharedState.items;
  }

  get mattockDurability() {
    return this._mattock_durability;
  }

  set mattockDurability(value: number) {
    const previous = this._mattock_durability;
    this._mattock_durability = value;
    this.emit('mattockdurabilitychange', value, previous);
  }

  get maxMattockDurability() {
    if (this.hasItem('bestMattock')) {
      return 100;
    }

    if (this.hasItem('betterMattock')) {
      return 5;
    }

    if (this.hasItem('mattock')) {
      return 2;
    }

    return 0;
  }

  get candleDurability() {
    return this._candle_durability;
  }

  set candleDurability(value: number) {
    this._candle_durability = value;
    this.emit('candledurabilitychange', value);
  }

  get maxCandleDurability() {
    return 3;
  }

  get currentRoomKey() {
    return this._currentRoomKey;
  }

  set currentRoomKey(value: string) {
    this._currentRoomKey = value;

    const roomConfig = FLOOR1.find((c) => c.key === value);
    if (roomConfig?.checkpoint) {
      // Shouldn't fail but who knows
      this._checkpointRoomKey = value;
    }

    this._roomHistory.unshift(value);
    this._roomHistory = this._roomHistory.toSpliced(5, 1);

    this.emit('currentroomkeychanged', value);
    this.persist();
  }

  get checkpointRoomKey() {
    return this._checkpointRoomKey;
  }

  get roomHistory() {
    return this._roomHistory;
  }

  get volume() {
    return this._volume;
  }

  set volume(value: number) {
    this._volume = value;
    this.emit('volumechanged', value);
    this.persist();
  }

  hasItem(type: string) {
    return this.sharedState.items.has(type);
  }

  addItem(type: string) {
    const mods: SharedStateModification[] = [];

    // Only one mattock allowed
    let replaceSubweapon = false;
    if (type === 'betterMattock') {
      mods.push({ type: 'removeItem', item: 'mattock' });
      replaceSubweapon = this.subweapon === 'mattock';
    } else if (type === 'bestMattock') {
      mods.push({ type: 'removeItem', item: 'mattock' });
      mods.push({ type: 'removeItem', item: 'betterMattock' });
      replaceSubweapon = this.subweapon === 'mattock' || this.subweapon === 'betterMattock';
    }

    mods.push({ type: 'addItem', item: type });

    for (const mod of mods) {
      this.sharedState.applyModification(mod);
    }

    if (replaceSubweapon) {
      this.subweapon = type;
    }

    // Picking up a mattock resets your mattock durability
    if (['mattock', 'betterMattock', 'bestMattock'].includes(type)) {
      this.mattockDurability = this.maxMattockDurability;
    }

    if (this._subweapon === null && C.ITEMS[type as C.ItemKey]?.isSubweapon) {
      this.subweapon = type as C.ItemKey;
    }
  }

  removeItem(type: string) {
    this.sharedState.applyModification({ type: 'removeItem', item: type });

    if (this.subweapon === type) {
      this.subweapon =
        Array.from(this.sharedState.items).find((key): key is C.ItemKey => C.ITEMS[key as C.ItemKey]?.isSubweapon) ??
        null;
    }
  }

  hasKey(room: string) {
    return this.sharedState.foundKeys.has(room);
  }

  addKey(room: string) {
    this.sharedState.applyModification({ type: 'addKey', room });
  }

  get keyCount() {
    return this.sharedState.keyCount;
  }

  useKey() {
    this.sharedState.applyModification({ type: 'useKey' });
  }

  isRoomSolved(roomKey: string) {
    return this.sharedState.solvedRooms.has(roomKey);
  }

  solveRoom(roomKey: string) {
    this.sharedState.applyModification({ type: 'solveRoom', room: roomKey });
  }

  getFlag(key: keyof Flags) {
    return this.sharedState.flags[key];
  }

  setFlag(key: keyof Flags, value: boolean) {
    this.sharedState.applyModification({ type: 'setFlag', flag: key, value });
  }

  addRoomItem(roomKey: string, item: EntityConfig) {
    this.sharedState.applyModification({ type: 'addRoomItem', room: roomKey, roomItem: item });
  }

  getRoomItems(roomKey: string) {
    return this.sharedState.roomItems[roomKey] ?? [];
  }

  removeRoomItem(roomKey: string, itemId: string) {
    this.sharedState.applyModification({ type: 'removeRoomItem', room: roomKey, itemId });
  }

  getOpenedDoors(roomKey: string) {
    return this.sharedState.openedDoors[roomKey] ?? { up: false, down: false, left: false, right: false };
  }

  setOpenedDoor(roomKey: string, direction: Direction, value: boolean) {
    this.sharedState.applyModification({ type: 'setOpenedDoor', room: roomKey, direction, value });
  }

  get subweaponsDisabled() {
    return this._subweaponsDisabled;
  }

  set subweaponsDisabled(value: boolean) {
    this._subweaponsDisabled = value;
    this.emit('subweaponsdisabledchanged', value);
  }

  deathReset() {
    this.hp = this.maxHp;
    this._roomHistory = [];
    this.subweaponsDisabled = false;
    this.mattockDurability = this.maxMattockDurability;
    this.candleDurability = this.maxCandleDurability;
  }

  resetSave() {
    this._subweapon = null;
    this._checkpointRoomKey = 'start';
    this.persist();

    const msg: WorkerMessage = {
      type: 'clearStorage',
    };
    worker.port.postMessage(msg);
    this.sharedState.reset();
  }
}

export const sharedState = new SharedState();

const gameState = new GameState(sharedState);

if (window.location.search.includes('reset')) {
  localStorage.removeItem(C.GAMESTORAGE_KEY);
} else {
  const persistedState = localStorage.getItem(C.GAMESTORAGE_KEY);
  if (persistedState !== null) {
    try {
      gameState.applyPersisted(JSON.parse(persistedState));
    } catch (err) {
      console.error('Could not parse stored game state, ignoring.');
    }
  }
}

export default gameState;
